import { Stack } from '@startuptools/ui/Stack';
import { Card } from '@startuptools/ui/Card';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Typography } from '@startuptools/ui/Typography';
import { ButtonGroup } from '@startuptools/ui/ButtonGroup';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { IconButton } from '@startuptools/ui/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { GqlAgendaItem, GqlAgendaItemDecision, GqlMeeting } from '../../../../graphql/base-types.graphql';
import { Trans } from '@lingui/react';
import { RichUserText } from '@startuptools/ui/RichUserText';
import { useEffect, useState } from 'react';
import { PromptDialog } from '../../../../components/simple-dialogs/PromptDialog';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useCreateAidMutation, useDeleteAidMutation, useUpdateAidMutation } from './Standard.graphql';
import { BoardMeetingDocument } from '../../../board-meetings/BoardMeeting.graphql';
import { ShareholdersMeetingDocument } from '../../../shareholders-meetings/ShareholdersMeeting.graphql';
import { RichTextInput } from '@startuptools/ui/RichTextInput';

type FormValues = {
  decisionPrimaryLang: string | null;
  decisionSecondaryLang: string | null;
};

export const AiStandard = ({
  meeting,
  agendaItem,
}: {
  meeting: Pick<GqlMeeting, 'primaryLang' | 'secondaryLang' | 'companyId'>;
  agendaItem: Pick<GqlAgendaItem, 'id'> & {
    agendaItemDecisions: Pick<
      GqlAgendaItemDecision,
      'id' | 'order' | 'decisionPrimaryLang' | 'decisionSecondaryLang'
    >[];
  };
}) => {
  const [showDecisionDialog, setShowDecisionDialog] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });

  const [doCreate, { loading: createLoading }] = useCreateAidMutation({
    refetchQueries: [BoardMeetingDocument, ShareholdersMeetingDocument],
  });
  const [doDelete, { loading: deleteLoading }] = useDeleteAidMutation({
    refetchQueries: [BoardMeetingDocument, ShareholdersMeetingDocument],
  });
  const [doUpdate, { loading: updateLoading }] = useUpdateAidMutation({
    refetchQueries: [BoardMeetingDocument, ShareholdersMeetingDocument],
  });

  const { control, setValue, handleSubmit } = useForm<FormValues>();

  useEffect(() => {
    if (showDecisionDialog.id) {
      const decision = agendaItem.agendaItemDecisions.find(d => d.id === showDecisionDialog.id);
      setValue('decisionPrimaryLang', decision?.decisionPrimaryLang ?? null);
      setValue('decisionSecondaryLang', decision?.decisionSecondaryLang ?? null);
    } else {
      setValue('decisionPrimaryLang', null);
      setValue('decisionSecondaryLang', null);
    }
  }, [setValue, showDecisionDialog, agendaItem.agendaItemDecisions]);

  const onSubmit: SubmitHandler<FormValues> = async values => {
    const decisionPrimaryLang = values.decisionPrimaryLang;
    const decisionSecondaryLang = values.decisionSecondaryLang;
    if (showDecisionDialog.id) {
      await doUpdate({
        variables: {
          companyId: meeting.companyId,
          input: { id: showDecisionDialog.id, ...values, decisionPrimaryLang, decisionSecondaryLang },
        },
      });
    } else {
      await doCreate({
        variables: {
          companyId: meeting.companyId,
          input: { agendaItemId: agendaItem.id, ...values, decisionPrimaryLang, decisionSecondaryLang },
        },
      });
    }
    setShowDecisionDialog({ open: false, id: null });
  };

  return (
    <>
      <Stack gap={1} mt={2}>
        <Box>
          <Button variant="outlined" onClick={() => setShowDecisionDialog({ open: true, id: null })}>
            <Trans id="Add decision" />
          </Button>
        </Box>
        {agendaItem.agendaItemDecisions.map(d => (
          <Card key={d.id} sx={{ flexGrow: 1 }}>
            <Stack direction="row" gap={1} justifyContent="space-between" flexGrow={1}>
              <Box>
                {meeting.primaryLang && (
                  <>
                    <Typography component="span" level="title-md">
                      <Trans id="Decision:" />
                    </Typography>
                    <RichUserText>{d.decisionPrimaryLang}</RichUserText>
                  </>
                )}
                {meeting.secondaryLang && (
                  <>
                    <Typography component="span" level="title-md">
                      <Trans id="Decision (eng):" />
                    </Typography>
                    <RichUserText>{d.decisionSecondaryLang}</RichUserText>
                  </>
                )}
              </Box>
              <Box>
                <ButtonGroup>
                  <IconButton loading={deleteLoading} onClick={() => setShowDecisionDialog({ open: true, id: d.id })}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="danger"
                    onClick={() => doDelete({ variables: { companyId: meeting.companyId, id: d.id } })}
                    loading={deleteLoading}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ButtonGroup>
              </Box>
            </Stack>
          </Card>
        ))}
      </Stack>
      {showDecisionDialog.open && (
        <PromptDialog
          modalDialogProps={{ minWidth: 800, maxWidth: 800 }}
          loading={createLoading || updateLoading}
          title={showDecisionDialog.id ? <Trans id="Edit decision" /> : <Trans id="Add decision" />}
          onCancel={() => setShowDecisionDialog({ open: false, id: null })}
          onSubmit={handleSubmit(onSubmit)}
        >
          {meeting.primaryLang && (
            <Controller
              control={control}
              name="decisionPrimaryLang"
              render={({ field: { value, onChange } }) => (
                <FormControl>
                  <FormLabel>
                    <Trans id="Decision" />
                  </FormLabel>
                  <RichTextInput value={value} onChange={onChange} />
                </FormControl>
              )}
            />
          )}
          {meeting.secondaryLang && (
            <Controller
              control={control}
              name="decisionSecondaryLang"
              render={({ field: { value, onChange } }) => (
                <FormControl>
                  <FormLabel>
                    <Trans id="Decision (eng)" />
                  </FormLabel>
                  <RichTextInput value={value} onChange={onChange} />
                </FormControl>
              )}
            />
          )}
        </PromptDialog>
      )}
    </>
  );
};
